import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AccountingInvoiceSyncStatusFailureCodeEnum } from 'anchor-ts-infra/src/dto/accounting-invoice/model';
import ContactSupportButton from '~/components/ContactSupportButton';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import { getAccountingDetails } from '~/integrations/Accounting/accounting.utils';
import { Accounting } from '~/types/accounting';
import { helpArticles } from './helpArticles';
const getContactSupportButton = (capitalize)=>/*#__PURE__*/ _jsx(ContactSupportButton, {
        testId: "contact-support",
        capitalize: capitalize
    });
const getReadMoreButton = (link, isLowercase)=>/*#__PURE__*/ _jsx(ExternalLinkButton, {
        openInNewTab: true,
        testId: "read-more",
        href: link,
        bi: {
            name: 'accounting_error_read_more'
        },
        children: isLowercase ? 'read more' : 'Read more'
    });
var FailureType = /*#__PURE__*/ function(FailureType) {
    FailureType["Invoice"] = "Invoice";
    FailureType["Payment"] = "Payment";
    FailureType["Payout"] = "Payout";
    FailureType["Refund"] = "Refund";
    return FailureType;
}(FailureType || {});
export function getPayoutAccountingErrorMessage(payoutIntegration, accounting) {
    return payoutIntegration.accept({
        whenSyncFailed: (data)=>getFailureMessage({
                failure: data,
                failureType: "Payout",
                accounting
            }),
        whenPendingSync: ()=>null,
        whenSyncInProgress: ()=>null,
        whenSynced: ()=>null,
        whenDeleted: ()=>null
    });
}
export function getAccountingInvoiceErrorMessage(param) {
    let { invoiceIntegration, accounting } = param;
    const nullFnc = ()=>null;
    return invoiceIntegration.accept({
        whenInvoiceSyncFailed: (data)=>getFailureMessage({
                failure: data.failure,
                failureType: "Invoice",
                accounting
            }),
        whenRefundSyncFailed: (data)=>getFailureMessage({
                failure: data.failure,
                failureType: "Refund",
                accounting
            }),
        whenPaymentSyncFailed: (data)=>getFailureMessage({
                failure: data.failure,
                failureType: "Payment",
                accounting
            }),
        whenPendingSync: nullFnc,
        whenInvoiceRecorded: nullFnc,
        whenVoidedRecorded: nullFnc,
        whenPaymentRecorded: nullFnc,
        whenRefundRecorded: nullFnc
    });
}
function getFailureMessage(param) {
    let { failure, failureType, accounting } = param;
    const param1 = failure.failureParam();
    switch(failure.failureCode()){
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrUnknown:
            return renderUnclassifiedFailure({
                failure,
                failureType,
                accounting
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrDuplicateDocNumber:
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    failureType,
                    " sync failed because this invoice number is already in use in",
                    ' ',
                    getAccountingDetails(accounting).fullName,
                    ". Change the invoice number in",
                    ' ',
                    getAccountingDetails(accounting).fullName,
                    ", then click “Retry sync.” To prevent this from happening again, ",
                    getReadMoreButton(getAccountingDetails(accounting).helpArticle.changeInvoicePrefix, true),
                    "."
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrAmountCalculationIncorrect:
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    failureType,
                    " sync failed. ",
                    getContactSupportButton(true),
                    " for assistance."
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCustomerDeleted:
            if (accounting === Accounting.Xero) {
                return /*#__PURE__*/ _jsxs("div", {
                    children: [
                        failureType,
                        " sync failed because the customer assigned was archived in",
                        ' ',
                        getAccountingDetails(accounting).fullName,
                        ". Restore ",
                        param1,
                        ' and then click "Retry sync."',
                        ' ',
                        getReadMoreButton(getAccountingDetails(accounting).helpArticle.reactivateItems),
                        "."
                    ]
                });
            }
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    failureType,
                    " sync failed because the customer assigned is inactive in",
                    ' ',
                    getAccountingDetails(accounting).fullName,
                    ". Reactivate ",
                    param1,
                    ' and then click "Retry sync."',
                    ' ',
                    getReadMoreButton(getAccountingDetails(accounting).helpArticle.reactivateItems),
                    "."
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrItemDeleted:
            if (accounting === Accounting.Xero) {
                return /*#__PURE__*/ _jsxs("div", {
                    children: [
                        failureType,
                        " sync failed because the assigned service ",
                        param1,
                        " was deleted in",
                        ' ',
                        getAccountingDetails(accounting).fullName,
                        " and cannot be restored. To resolve, map the service in Anchor to a different service in ",
                        getAccountingDetails(accounting).fullName,
                        ', then click "Retry sync".'
                    ]
                });
            }
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    failureType,
                    " sync failed because the service assigned is inactive in",
                    ' ',
                    getAccountingDetails(accounting).fullName,
                    ". Reactivate ",
                    param1,
                    ' and then click "Retry sync."',
                    ' ',
                    getReadMoreButton(getAccountingDetails(accounting).helpArticle.reactivateItems),
                    "."
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrClassDeleted:
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    failureType,
                    " sync failed because the class assigned is inactive. Reactivate ",
                    param1,
                    " in",
                    ' ',
                    getAccountingDetails(accounting).fullName,
                    ' and then click "Retry sync."',
                    ' ',
                    getReadMoreButton(getAccountingDetails(accounting).helpArticle.reactivateItems),
                    "."
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrInvoiceAmountNotMatching:
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    failureType,
                    " sync failed because the amount in ",
                    getAccountingDetails(accounting).fullName,
                    " was changed and no longer matches the invoice amount in Anchor. Fix the invoice amount in",
                    ' ',
                    getAccountingDetails(accounting).fullName,
                    ' and then click "Retry sync."'
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrObjectNotFound:
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    failureType,
                    " sync failed because an account, customer, item, or vendor you are trying to use has been made inactive. Review and reactivate in ",
                    getAccountingDetails(accounting).fullName,
                    ' and then click "Retry sync."',
                    ' ',
                    getReadMoreButton(getAccountingDetails(accounting).helpArticle.reactivateItems),
                    "."
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrAccountDeleted:
            if (accounting === Accounting.Xero) {
                return /*#__PURE__*/ _jsxs("div", {
                    children: [
                        failureType,
                        " sync failed because the assigned account was archived in",
                        ' ',
                        getAccountingDetails(accounting).fullName,
                        ". Restore ",
                        param1,
                        ' and then click "Retry sync."',
                        ' ',
                        getReadMoreButton(getAccountingDetails(accounting).helpArticle.reactivateItems),
                        "."
                    ]
                });
            }
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    failureType,
                    " sync failed because the assigned account is inactive in",
                    ' ',
                    getAccountingDetails(accounting).fullName,
                    ". Reactivate ",
                    param1,
                    ' and then click "Retry sync."',
                    ' ',
                    getReadMoreButton(getAccountingDetails(accounting).helpArticle.reactivateItems),
                    "."
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrVendorDeleted:
            if (accounting === Accounting.Xero) {
                return /*#__PURE__*/ _jsxs("div", {
                    children: [
                        failureType,
                        " sync failed because the assigned contact ",
                        param1,
                        " was archived in",
                        ' ',
                        getAccountingDetails(accounting).fullName,
                        ". Restore ",
                        param1,
                        ' and then click "Retry sync."',
                        ' ',
                        getReadMoreButton(getAccountingDetails(accounting).helpArticle.reactivateItems),
                        "."
                    ]
                });
            }
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    failureType,
                    " sync failed because the assigned vendor is inactive in",
                    ' ',
                    getAccountingDetails(accounting).fullName,
                    ". Reactivate ",
                    param1,
                    ' and then click "Retry sync."',
                    ' ',
                    getReadMoreButton(getAccountingDetails(accounting).helpArticle.reactivateItems),
                    "."
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrInvoicePaid:
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    failureType,
                    " sync failed because a payment for this invoice has already been created in",
                    ' ',
                    getAccountingDetails(accounting).fullName,
                    '. Remove the payment and then click "Retry sync".'
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrDepositTotalAmountNotMatching:
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    failureType,
                    " sync failed because the payout amount does not match the deposit amount in",
                    ' ',
                    getAccountingDetails(accounting).fullName,
                    ". ",
                    getContactSupportButton(true),
                    " for further assistance."
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrBusinessValidationTransactionCannotBeApplied:
            {
                const paramLink = failure.paramLink();
                return /*#__PURE__*/ _jsxs("div", {
                    children: [
                        failureType,
                        " sync failed because the payment ",
                        param1,
                        " is already applied to a",
                        ' ',
                        getAccountingDetails(accounting).fullName,
                        ' ',
                        (paramLink === null || paramLink === void 0 ? void 0 : paramLink.url) ? /*#__PURE__*/ _jsx(ExternalLinkButton, {
                            openInNewTab: true,
                            testId: "deposit-link",
                            href: paramLink.url,
                            bi: {
                                name: 'accounting_error_deposit_link'
                            },
                            children: "deposit"
                        }) : 'deposit',
                        ". To resolve the issue, find the deposit in ",
                        getAccountingDetails(accounting).fullName,
                        ', uncheck the payment, save, and then click "Retry sync" in Anchor.',
                        ' ',
                        getReadMoreButton(getAccountingDetails(accounting).helpArticle.unlinkPayment),
                        "."
                    ]
                });
            }
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrInvoiceDeleted:
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    failureType,
                    " sync failed because the invoice could not be found in",
                    ' ',
                    getAccountingDetails(accounting).fullName,
                    ". It might have been deleted or modified. Please note that as part of the ",
                    failureType.toLowerCase(),
                    " sync retry, the invoice will also be re-synced.",
                    ' ',
                    getContactSupportButton(true),
                    " for further assistance."
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrPaymentDeleted:
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    failureType,
                    " sync failed because the payment ",
                    param1,
                    " could not be found in",
                    ' ',
                    getAccountingDetails(accounting).fullName,
                    ". It might be deleted or modified.",
                    ' ',
                    "You can try resyncing ".concat(accounting === Accounting.QBO ? 'both the invoice and the payment' : 'the payment', " directly from the invoice page. "),
                    getContactSupportButton(true),
                    " for further assistance."
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrAccountPeriodClosed:
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    failureType,
                    " sync failed because the account period has closed in",
                    ' ',
                    getAccountingDetails(accounting).fullName,
                    ", preventing updates to the account books. To resolve, either turn \"Close the books\" off or change the closing date. Then, click 'Retry sync' in Anchor.",
                    ' ',
                    getReadMoreButton(getAccountingDetails(accounting).helpArticle.accountPeriodClosed),
                    "."
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrInvalidCompanyStatus:
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    failureType,
                    " sync failed due to an issue with the ",
                    getAccountingDetails(accounting).fullName,
                    ' ',
                    "account subscription. Please update your account subscription in",
                    ' ',
                    getAccountingDetails(accounting).fullName,
                    ' and then click "Retry Sync".'
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrMissingPermissions:
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    failureType,
                    " sync failed due to missing permissions on ",
                    getAccountingDetails(accounting).fullName,
                    ". Please make sure your user in ",
                    getAccountingDetails(accounting).fullName,
                    " has the needed access rights for this action."
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.ClassTrackingDisabledWithOverride:
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    "There are specific class overrides defined in the agreement, but the class feature is currently disabled in ",
                    getAccountingDetails(accounting).fullName,
                    ". Please enable this feature in",
                    ' ',
                    getAccountingDetails(accounting).fullName,
                    " or remove the class overrides from the agreement."
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrItemIsCategory:
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    failureType,
                    " sync failed because the service assigned is set up as a category instead of a product or service in ",
                    getAccountingDetails(accounting).fullName,
                    ". Edit ",
                    param1,
                    ' and then click "Retry sync."'
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodeInvoiceClientNotMapped:
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    failureType,
                    " sync failed. Contact mapping with ",
                    getAccountingDetails(accounting).fullName,
                    " is not completed. Complete mapping and retry sync.",
                    ' ',
                    getReadMoreButton(getAccountingDetails(accounting).helpArticle.contactMapping)
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodeInvoiceServiceNotMapped:
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    failureType,
                    " sync failed. Service mapping with ",
                    getAccountingDetails(accounting).fullName,
                    " is not completed. Complete mapping and retry sync.",
                    ' ',
                    getReadMoreButton(getAccountingDetails(accounting).helpArticle.servicesMapping)
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodeInvoiceClientAndServiceNotMapped:
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    failureType,
                    " sync failed. Contact and service mapping with",
                    ' ',
                    getAccountingDetails(accounting).fullName,
                    " is not completed. Complete mapping and retry sync.",
                    ' ',
                    getReadMoreButton(getAccountingDetails(accounting).helpArticle.setup)
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodePayoutTransactionsNotSynced:
            {
                const invoicesToSync = failure.invoicesToSync();
                const paymentsToSync = failure.paymentsToSync();
                const useInvoicesToSync = invoicesToSync.length > 0;
                const itemsToSyncText = (useInvoicesToSync ? invoicesToSync : paymentsToSync).map((inv)=>inv.formattedNumber).join(', ');
                const itemTypeText = useInvoicesToSync ? 'the following invoices' : 'the payments for the following invoices';
                const message = "".concat(failureType, " sync failed. Please sync ").concat(itemTypeText, " to ").concat(getAccountingDetails(accounting).fullName, " and retry sync: ").concat(itemsToSyncText, ".");
                return /*#__PURE__*/ _jsx("div", {
                    children: message
                });
            }
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrTimeActivityCannotAppliedToInvoice:
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    failureType,
                    " sync failed because an assigned time activity was modified, deleted, or already invoiced in ",
                    getAccountingDetails(accounting).fullName,
                    "."
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrInvoiceCreationLockDate:
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    failureType,
                    " sync failed because the period is locked in",
                    ' ',
                    getAccountingDetails(accounting).fullName,
                    ". To resolve, remove or adjust the lock date in",
                    ' ',
                    getAccountingDetails(accounting).fullName,
                    ", then click Retry sync in Anchor.",
                    ' ',
                    getReadMoreButton(helpArticles.xeroCreationLocked()),
                    "."
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.RequestTimeout:
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    failureType,
                    " sync is taking longer than expected. We’re automatically retrying the sync in the background - no action is needed from you right now. Please check back later, and if the issue persists. ",
                    getContactSupportButton(true),
                    " for further assistance."
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrRateLimitExceeded:
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    failureType,
                    " sync failed because the rate limit has been exceeded in",
                    ' ',
                    getAccountingDetails(accounting).fullName,
                    ". Please try again later."
                ]
            });
        case AccountingInvoiceSyncStatusFailureCodeEnum.RefundAmountNotMatching:
            {
                return /*#__PURE__*/ _jsxs("div", {
                    children: [
                        failureType,
                        " sync failed because the refund amount does not match for",
                        ' ',
                        getAccountingDetails(accounting).fullName,
                        ". ",
                        getContactSupportButton(true),
                        " for further assistance."
                    ]
                });
            }
        default:
            return renderUnclassifiedFailure({
                failure,
                failureType,
                accounting
            });
    }
}
function renderUnclassifiedFailure(param) {
    let { failure, failureType, accounting } = param;
    const detail = failure.rawDetail();
    if (detail !== undefined) {
        return /*#__PURE__*/ _jsxs("div", {
            children: [
                failureType,
                " sync failed in ",
                getAccountingDetails(accounting).fullName,
                ": ",
                detail,
                ' ',
                getContactSupportButton(true),
                " if the issue persists."
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            failureType,
            " sync failed in ",
            getAccountingDetails(accounting).fullName,
            ". ",
            getContactSupportButton(true),
            ' ',
            "for assistance."
        ]
    });
}
